import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { string, bool, arrayOf, shape, objectOf } from "prop-types";

import cx from "classnames";

import initialState from "./initialState.js";

import fetchSearchResults from "../../common/fetchSearchResults.js";
import {
  toggleFacet,
  clearFacetGroup,
  initFacets,
  initCurrentParams,
  createQueryString,
} from "../../common/dynamicSearch.js";

import { scrollToId } from "../../common/scrollHelpers.js";

import ArrowLink from "../ArrowLink/ArrowLink.jsx";

import Pagination from "../Pagination/Pagination.jsx";
import FilterBar from "../FilterBar/FilterBar.jsx";
import ResultsInfo from "../ResultsInfo/ResultsInfo.jsx";
import ManualContentListing from "../ManualContentListing/ManualContentListing.jsx";
import GranteeListing from "../GranteeListing/GranteeListing.jsx";
import Sorters from "../Sorters/Sorters.jsx";
import SimpleSearchBox from "../SimpleSearchBox/SimpleSearchBox.jsx";
import ToggleGroup from "../ToggleGroup/ToggleGroup.jsx";
import SectionHeadline from "../SectionHeadline/SectionHeadline.jsx";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner.jsx";
import SelectedFilters from "../SearchParts/SelectedFilters/SelectedFilters.jsx";

// USP specific Renderings
import RFPListing from "../RFPListing/RFPListing.jsx";

const propTypes = {
  id: string.isRequired,
  title: string,
  enableQueryString: bool,
  subTitle: string,
  pagination: bool,
  search: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  type: string,
  dictionary: objectOf(string).isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    excludedTaxonomy: string,
    loadAllPages: bool,
    pageId: string,
  }).isRequired,
  seeMore: shape({ href: string, text: string }),
  sorters: arrayOf(
    shape({
      direction: string,
      label: string,
      value: string,
      isActive: bool,
      id: string,
    }),
  ),
};

const DynamicContentListing = (props) => {
  const {
    id,
    title,
    subTitle,
    enableQueryString,
    pagination,
    search,
    hideMobileSummary,
    url,
    type,
    dictionary,
    query,
    seeMore,
    sorters,
  } = props;

  const activeSorterParam = useMemo(() => {
    const activeSorter = sorters.find((sorter) => sorter.isActive) || null;

    return activeSorter
      ? {
          sortBy: activeSorter.value,
          sortOrder: activeSorter.direction,
        }
      : null;
  }, [sorters]);

  const generalParams = {
    ...initialState.query,
    ...query,
    ...activeSorterParam,
  };

  const [showOverlay, setShowOverlay] = useState(false);

  const [selectedFacets, setSelectedFacets] = useState(
    initFacets(window.location.search),
  );
  const [serverData, setServerData] = useState(null);

  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  useEffect(() => {
    if (new URL(document.location).searchParams.get("q")) {
      scrollToId(id);
      setTimeout(() => {
        document.querySelector(`#${id} .simple-search-box__input`).focus();
      }, 1000);
    }
  }, [id]);

  //update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      const queryString = createQueryString(currentParams, selectedFacets);
      if (queryString.length > 0) {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            "?" +
            createQueryString(currentParams, selectedFacets) +
            window.location.hash,
        );
      } else {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            window.location.hash,
        );
      }
    }
  }, [currentParams, enableQueryString, selectedFacets]);

  const handleQueryChange = (term) => {
    if (currentParams.q === term) {
      return;
    }

    setCurrentParams((prevState) => {
      return {
        ...prevState,
        q: term,
        page: 1,
        sortBy: "relevance",
        sortOrder: null,
      };
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults(
      url,
      {
        ...currentParams,
        ...selectedFacets,
      },
      type,
      signal,
    )
      .then(function (data) {
        if (data) {
          setServerData(data);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, selectedFacets, type, url]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change
      resultsFocusRef.current.focus();
      showFocusRef.current.showFocus = false;
    }
  }, [serverData]);

  const handlePaginationChange = (page) => {
    showFocusRef.current.showFocus = true;
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
  };

  const handleFacetChange = useCallback((facetGroupValue, facet) => {
    setCurrentParams((prevState) => {
      return {
        ...prevState,
        page: 1,
      };
    });

    setSelectedFacets((prevState) => {
      return toggleFacet(facetGroupValue, facet, prevState);
    });
  }, []);

  const handleClearFacetGroup = (facetGroupValue) => {
    setSelectedFacets((prevState) => {
      return clearFacetGroup(facetGroupValue, prevState);
    });
  };

  const handleSorterClick = (params) => {
    setCurrentParams((prevState) => {
      return { ...prevState, ...params, page: 1 };
    });
  };

  const renderResults = useMemo(() => {
    if (!serverData) {
      return null;
    }

    switch (type) {
      case "grantee":
        return (
          <GranteeListing
            granteeWebsiteText={dictionary.granteeWebsite}
            granteeReportText={dictionary.granteeReport}
            items={serverData.results}
            focusRef={resultsFocusRef}
          />
        );
      case "rfp":
        return (
          <RFPListing
            applyLinkText={dictionary.applyLink}
            detailsLinkText={dictionary.detailsLink}
            applicationDueText={dictionary.applicationDue}
            items={serverData.results}
            focusRef={resultsFocusRef}
          />
        );
      default:
        return (
          <ManualContentListing
            results={serverData.results}
            focusRef={resultsFocusRef}
          />
        );
    }
  }, [
    dictionary.applicationDue,
    dictionary.applyLink,
    dictionary.detailsLink,
    dictionary.granteeReport,
    dictionary.granteeWebsite,
    serverData,
    type,
  ]);

  const classModifier = `dynamic-content-listing--${type}`;

  return (
    <div className={`dynamic-content-listing ${classModifier}`} id={id}>
      {title && (
        <SectionHeadline
          title={title}
          subtitle={subTitle}
          size="small"
          noBorder={true}
        />
      )}
      {search && (
        <div className="dynamic-content-listing__search-box">
          <SimpleSearchBox
            searchTerm={currentParams.q}
            onTextChange={handleQueryChange}
            placeholder={dictionary.searchPlaceholder}
          />
        </div>
      )}
      {serverData &&
        serverData.facets &&
        serverData.facets.length > 0 &&
        (type === "default" || type === "grantee") && (
          <>
            <div className="dynamic-content-listing__filter-bar">
              <FilterBar
                filterTitle={dictionary.filterTitle}
                filterLabel={dictionary.filterLabel}
                onFacetChange={handleFacetChange}
                facets={serverData.facets}
                onClearFacetGroup={handleClearFacetGroup}
                selectedFacets={selectedFacets}
              />
            </div>
            {selectedFacets && JSON.stringify(selectedFacets) !== "{}" && (
              <div className="dynamic-content-listing__selected-filters">
                <SelectedFilters
                  label={dictionary?.selectedFilters}
                  clearLabel={dictionary?.clearAll}
                  facets={serverData?.facets}
                  onClearItem={handleFacetChange}
                  onClearAll={() => {
                    setSelectedFacets({});
                  }}
                />
              </div>
            )}
          </>
        )}
      {serverData && serverData.facets && type === "rfp" && (
        <div className="dynamic-content-listing__toggle-bar">
          <div className="dynamic-content-listing__toggle-group-container">
            {serverData.facets
              //rfp listing only displays facets with id "fostatus"
              .filter((facet) => facet.id === "fostatus")
              .map((facet, i) => {
                if (facet.values.length > 0)
                  return (
                    <ToggleGroup
                      key={facet.id}
                      id={facet.id}
                      values={facet.values}
                      onFacetChange={handleFacetChange}
                    />
                  );
              })}
          </div>
          {sorters && sorters.length > 0 && (
            <div className="dynamic-content-listing__sorters">
              <Sorters
                onSorterClick={handleSorterClick}
                //rfp listing should not display sort by date
                sorters={sorters.filter((sorter) => sorter.value !== "date")}
                sortBy={currentParams.sortBy}
                sortDirection={currentParams.sortOrder}
              />
            </div>
          )}
        </div>
      )}

      {serverData && (type === "default" || type === "grantee") && (
        <div className="dynamic-content-listing__sorters-wrapper">
          <div className="dynamic-content-listing__results-info">
            <ResultsInfo
              totalResults={serverData.totalResults}
              request={serverData.request}
              resultsInfoLabel={dictionary.resultsInfoLabel}
              query={currentParams.q}
              clearAllFacetsLabel={dictionary.clearAll}
            />
          </div>

          {sorters && sorters.length > 0 && (
            <div className="dynamic-content-listing__sorters">
              <Sorters
                onSorterClick={handleSorterClick}
                sorters={sorters}
                sortBy={currentParams.sortBy}
                sortDirection={currentParams.sortOrder}
              />
            </div>
          )}
        </div>
      )}
      <div aria-live="polite">
        {serverData && serverData.results && serverData.results.length < 1 && (
          <div className="dynamic-content-listing__no-results">
            {dictionary.noResultsMessage || "No Results Found"}
          </div>
        )}
      </div>
      {serverData && serverData.results && (
        <div
          className={cx("dynamic-content-listing__results", {
            "dynamic-content-listing__results--no-mobile-summaries":
              hideMobileSummary,
          })}
          id={`${id}-results`}
        >
          {showOverlay && <LoadingSpinner />}
          {!showOverlay && serverData.results.length > 0 && (
            <div aria-live="polite" className="u-visuallyhidden">
              New Results
            </div>
          )}
          {renderResults}
        </div>
      )}
      {serverData &&
        pagination &&
        serverData.totalResults > serverData.request.perPage && (
          <div
            className={cx("dynamic-content-listing__pagination", {
              "dynamic-content-listing__pagination--centered": !seeMore.href,
            })}
          >
            <Pagination
              totalResults={serverData.totalResults}
              //this is currently returned as a string from the server, so convert to number
              resultsPerPage={Number(serverData.request.perPage)}
              currentPage={Number(currentParams.page)}
              onPaginationChange={handlePaginationChange}
            />
            {seeMore.href && (
              <div className="dynamic-content-listing__more">
                <ArrowLink {...seeMore} gtmElementLabel="dcl see more link" />
              </div>
            )}
          </div>
        )}
    </div>
  );
};

DynamicContentListing.propTypes = propTypes;
export default DynamicContentListing;
